/**
 * TMJValidator — Safety checks for TMJ files before loading.
 *
 * Prevents memory exhaustion, UI freezes, and crashes from:
 *   - Absurdly large maps (e.g., 100000x100000)
 *   - Too many layers (hundreds of layers = OOM on tile arrays)
 *   - Enormous file sizes (gigabyte JSON strings)
 *   - Malformed data that could cause infinite loops
 *   - Non-TMJ JSON files disguised with .tmj extension
 */

// Limits — generous enough for real maps, strict enough to prevent abuse
const LIMITS = {
  maxFileSize:     50 * 1024 * 1024,  // 50 MB — covers even huge multi-tileset maps
  maxMapWidth:     5000,               // tiles
  maxMapHeight:    5000,               // tiles
  maxTotalTiles:   10_000_000,         // width × height — ~10M tiles max
  maxLayers:       200,                // enough for complex maps
  maxTilesets:     100,                // per map
  maxLayerData:    25_000_000,         // max elements in a single layer data array
};

/**
 * Validate a raw JSON string before parsing, and the parsed TMJ object after.
 *
 * @param {string} text — Raw file content (JSON string)
 * @param {string} [fileName] — For error messages
 * @returns {{ ok: boolean, error?: string, tmj?: object }}
 */
export function validateTMJ(text, fileName = 'file') {
  // ── Pre-parse: file size ──
  if (text.length > LIMITS.maxFileSize) {
    const sizeMB = (text.length / 1024 / 1024).toFixed(1);
    return {
      ok: false,
      error: `"${fileName}" is ${sizeMB} MB — max supported is ${(LIMITS.maxFileSize / 1024 / 1024).toFixed(0)} MB.`,
    };
  }

  // ── Parse ──
  let tmj;
  try {
    tmj = JSON.parse(text);
  } catch (err) {
    return {
      ok: false,
      error: `"${fileName}" is not valid JSON: ${err.message}`,
    };
  }

  // ── Post-parse: structure checks ──

  // Must be an object (not array, string, number, etc.)
  if (!tmj || typeof tmj !== 'object' || Array.isArray(tmj)) {
    return { ok: false, error: `"${fileName}" is not a valid map file (expected a JSON object).` };
  }

  // Must have basic TMJ fields
  if (typeof tmj.width !== 'number' || typeof tmj.height !== 'number') {
    return { ok: false, error: `"${fileName}" doesn't look like a Tiled map — missing width/height.` };
  }

  // Map dimensions
  if (tmj.width > LIMITS.maxMapWidth || tmj.height > LIMITS.maxMapHeight) {
    return {
      ok: false,
      error: `Map is ${tmj.width}×${tmj.height} tiles — max supported is ${LIMITS.maxMapWidth}×${LIMITS.maxMapHeight}.`,
    };
  }

  if (tmj.width <= 0 || tmj.height <= 0) {
    return { ok: false, error: `Map has invalid dimensions: ${tmj.width}×${tmj.height}.` };
  }

  const totalTiles = tmj.width * tmj.height;
  if (totalTiles > LIMITS.maxTotalTiles) {
    return {
      ok: false,
      error: `Map has ${totalTiles.toLocaleString()} tiles (${tmj.width}×${tmj.height}) — max is ${LIMITS.maxTotalTiles.toLocaleString()}.`,
    };
  }

  // Layer count (recursive)
  const layerCount = countLayers(tmj.layers || []);
  if (layerCount > LIMITS.maxLayers) {
    return {
      ok: false,
      error: `Map has ${layerCount} layers — max supported is ${LIMITS.maxLayers}.`,
    };
  }

  // Tileset count
  if (tmj.tilesets && tmj.tilesets.length > LIMITS.maxTilesets) {
    return {
      ok: false,
      error: `Map references ${tmj.tilesets.length} tilesets — max supported is ${LIMITS.maxTilesets}.`,
    };
  }

  // Layer data array sizes
  const dataError = checkLayerData(tmj.layers || []);
  if (dataError) {
    return { ok: false, error: dataError };
  }

  return { ok: true, tmj };
}

function countLayers(layers) {
  let count = 0;
  for (const layer of layers) {
    count++;
    if (layer.layers) count += countLayers(layer.layers);
  }
  return count;
}

function checkLayerData(layers) {
  for (const layer of layers) {
    if (layer.data && Array.isArray(layer.data)) {
      if (layer.data.length > LIMITS.maxLayerData) {
        return `Layer "${layer.name || 'untitled'}" has ${layer.data.length.toLocaleString()} tiles — max is ${LIMITS.maxLayerData.toLocaleString()}.`;
      }
    }
    if (layer.layers) {
      const err = checkLayerData(layer.layers);
      if (err) return err;
    }
  }
  return null;
}
